import { useState } from 'react';
import { format, subDays, startOfMonth, endOfMonth, subMonths } from 'date-fns';
import { CalendarIcon } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

interface DateRangePickerProps {
  dateFrom?: string;
  dateTo?: string;
  onChange: (dateFrom: string | undefined, dateTo: string | undefined) => void;
}

type PresetKey = 'custom' | 'last7' | 'last30' | 'last90' | 'thisMonth' | 'lastMonth';

const presets: { key: PresetKey; label: string }[] = [
  { key: 'last7', label: 'Last 7 days' },
  { key: 'last30', label: 'Last 30 days' },
  { key: 'last90', label: 'Last 90 days' },
  { key: 'thisMonth', label: 'This month' },
  { key: 'lastMonth', label: 'Last month' },
  { key: 'custom', label: 'Custom range' },
];

function getPresetDates(key: PresetKey): { from: Date; to: Date } | null {
  const now = new Date();
  switch (key) {
    case 'last7':
      return { from: subDays(now, 7), to: now };
    case 'last30':
      return { from: subDays(now, 30), to: now };
    case 'last90':
      return { from: subDays(now, 90), to: now };
    case 'thisMonth':
      return { from: startOfMonth(now), to: endOfMonth(now) };
    case 'lastMonth': {
      const lastMonth = subMonths(now, 1);
      return { from: startOfMonth(lastMonth), to: endOfMonth(lastMonth) };
    }
    default:
      return null;
  }
}

export function DateRangePicker({
  dateFrom,
  dateTo,
  onChange,
}: DateRangePickerProps) {
  const [preset, setPreset] = useState<PresetKey>('last30');
  const [showCustom, setShowCustom] = useState(false);

  const handlePresetChange = (value: string) => {
    const key = value as PresetKey;
    setPreset(key);

    if (key === 'custom') {
      setShowCustom(true);
      return;
    }

    setShowCustom(false);
    const dates = getPresetDates(key);
    if (dates) {
      onChange(
        format(dates.from, 'yyyy-MM-dd'),
        format(dates.to, 'yyyy-MM-dd')
      );
    }
  };

  const handleCustomDateChange = (
    type: 'from' | 'to',
    value: string
  ) => {
    if (type === 'from') {
      onChange(value || undefined, dateTo);
    } else {
      onChange(dateFrom, value || undefined);
    }
  };

  return (
    <div className="space-y-3">
      <div className="flex items-center gap-2">
        <CalendarIcon className="h-4 w-4 text-muted-foreground" />
        <span className="text-sm font-medium">Date Range</span>
      </div>

      <Select value={preset} onValueChange={handlePresetChange}>
        <SelectTrigger>
          <SelectValue placeholder="Select date range" />
        </SelectTrigger>
        <SelectContent>
          {presets.map((p) => (
            <SelectItem key={p.key} value={p.key}>
              {p.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {showCustom && (
        <div className="flex gap-2">
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">From</label>
            <Input
              type="date"
              value={dateFrom || ''}
              onChange={(e) => handleCustomDateChange('from', e.target.value)}
            />
          </div>
          <div className="flex-1">
            <label className="text-xs text-muted-foreground">To</label>
            <Input
              type="date"
              value={dateTo || ''}
              onChange={(e) => handleCustomDateChange('to', e.target.value)}
            />
          </div>
        </div>
      )}

      {!showCustom && dateFrom && dateTo && (
        <p className="text-xs text-muted-foreground">
          {format(new Date(dateFrom), 'MMM d, yyyy')} -{' '}
          {format(new Date(dateTo), 'MMM d, yyyy')}
        </p>
      )}

      {(dateFrom || dateTo) && (
        <Button
          variant="ghost"
          size="sm"
          className="h-7 text-xs"
          onClick={() => {
            setPreset('last30');
            setShowCustom(false);
            const dates = getPresetDates('last30');
            if (dates) {
              onChange(
                format(dates.from, 'yyyy-MM-dd'),
                format(dates.to, 'yyyy-MM-dd')
              );
            }
          }}
        >
          Reset to Last 30 days
        </Button>
      )}
    </div>
  );
}
